import {InfoCircleOutlined} from '@ant-design/icons';
import {Button, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import Empty from 'common/empty/Empty';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {cpcList} from 'store/slices/cpc/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import CampaignList from './components/CampaignList/CampaignList';

const CPC_LIST_PAGE_SIZE = 20;

const CpcList: React.FC = () => {
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {cpcHistoryList, loading} = useAppSelector((state) => state.cpc);
  const campaigns = cpcHistoryList?.cpcs ?? [];

  const loadCampaigns = useCallback(
    (page: number) => {
      return dispatch(cpcList({vendor_id: vendorId, page_size: CPC_LIST_PAGE_SIZE, page}));
    },
    [dispatch, vendorId]
  );

  const loadMore = () => {
    if (isLoadingMoreRef.current || !cpcHistoryList) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    void loadCampaigns(cpcHistoryList.page + 1)
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  };

  useEffect(() => {
    void loadCampaigns(1);
  }, [loadCampaigns]);

  const hasActiveCampaign = campaigns.some(
    (campaign) => campaign.state === 'initiated' || campaign.state === 'active'
  );
  const isInitialLoading = Boolean(loading) && campaigns.length === 0;

  return (
    <PageLayout
      contentId="cpc-list"
      footer={
        <div className="flex flex-col gap-2">
          <Button
            fullWidth
            disabled={hasActiveCampaign}
            onClick={() => navigate('/cpc/buy/range-picker')}
          >
            خرید کلیک
          </Button>
          {hasActiveCampaign ? (
            <p className="flex items-center gap-1 text-body-xs text-gray-08">
              <InfoCircleOutlined className="shrink-0" aria-hidden />
              در هر لحظه فقط یک کمپین می‌تواند فعال باشد.
            </p>
          ) : null}
        </div>
      }
    >
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : campaigns.length > 0 ? (
        <CampaignList
          campaigns={campaigns}
          hasMore={Boolean(cpcHistoryList && cpcHistoryList.page < cpcHistoryList.total_pages)}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
        />
      ) : (
        <Empty className="min-h-60 flex-1 gap-1 px-4 text-center">
          <p className="text-body-sm-emphasis text-neutral-fg">خریدی برای نمایش وجود ندارد</p>
          <p className="text-body-sm text-gray-07">تا به حال هیچ کلیکی نخریده‌اید!</p>
        </Empty>
      )}
    </PageLayout>
  );
};

export default CpcList;
